import { Announcement, AnnouncementPriority } from '../models/announcement.models';

/**
 * Pure helpers for `AnnouncementsWidgetComponent`, kept standalone so the ordering rule
 * is unit-testable without Angular or a mocked HTTP client.
 */

/** Unread, or read but still owing a required acknowledgment. Mirrors the bell's rule. */
export function needsAttention(a: Announcement): boolean {
  // Archived items never need attention (the bell excludes them too).
  return a.isActive && (!a.seen || (a.requiresAcknowledgment && !a.acknowledgedAt));
}

/** True only when the caller is themself a recipient still owing an acknowledgment. */
export function isAckPendingForCaller(a: Announcement): boolean {
  // An archived announcement can no longer be acknowledged, so it never owes one.
  return a.isActive && a.seen !== null && a.requiresAcknowledgment && !a.acknowledgedAt;
}

/** Browser (Intl) locale for the app's own language codes; Finnish when unknown. */
export function intlLocaleFor(lang: string | null | undefined): string {
  switch (lang) {
    case 'en': return 'en-US';
    case 'gb': return 'en-GB';
    case 'ee': return 'et-EE';
    default: return 'fi-FI';
  }
}

export function hasEditedMarker(a: Announcement): boolean {
  return !!a.editedAt;
}

/** publishedAt ?? createdAt — a scheduled announcement's createdAt predates its arrival. */
export function getAnnouncementDisplayDate(a: Announcement): string {
  return a.publishedAt ?? a.createdAt;
}

/**
 * Widget order: High priority still needing attention, then acknowledgment-pending,
 * then unread, then everything else — newest first within each group. `maxItems` caps
 * the result: the widget only ever shows a handful of recent items, and the cap must be
 * applied *after* reordering or a buried High item would never surface.
 */
export function orderWidgetAnnouncements(items: Announcement[], maxItems: number): Announcement[] {
  const tier = (a: Announcement): number => {
    if (a.priority === AnnouncementPriority.High && needsAttention(a)) return 0;
    if (isAckPendingForCaller(a)) return 1;
    if (!a.seen) return 2;
    return 3;
  };
  const byDateDesc = (a: Announcement, b: Announcement) =>
    new Date(getAnnouncementDisplayDate(b)).getTime() - new Date(getAnnouncementDisplayDate(a)).getTime();

  return [...items].sort((a, b) => tier(a) - tier(b) || byDateDesc(a, b)).slice(0, maxItems);
}
